import React, { useCallback, useMemo } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./TransitionTasksTablePagination.module.css";

interface TransitionTasksTablePaginationProps {
  readonly className?: string;
  readonly pageSize: number;
  readonly currentPage: number;
  readonly totalCount: number;
  readonly onPageChange: (page: number) => void;
  readonly isPrevButtonDisabled?: boolean;
  readonly isNextButtonDisabled?: boolean;
}

const TransitionTasksTablePagination: React.FC<
  TransitionTasksTablePaginationProps
> = ({
  className,
  pageSize,
  currentPage,
  totalCount,
  onPageChange,
  isPrevButtonDisabled,
  isNextButtonDisabled,
}) => {
  const displayInfo = useMemo(() => {
    const startIndex = currentPage * pageSize + 1;
    const endIndex = Math.min((currentPage + 1) * pageSize, totalCount);
    return `${startIndex}–${endIndex} of ${totalCount}`;
  }, [currentPage, pageSize, totalCount]);

  const navigationState = useMemo(
    () => ({
      isPrevDisabled: isPrevButtonDisabled ?? currentPage === 0,
      isNextDisabled:
        isNextButtonDisabled ?? (currentPage + 1) * pageSize >= totalCount,
    }),
    [
      isPrevButtonDisabled,
      isNextButtonDisabled,
      currentPage,
      pageSize,
      totalCount,
    ]
  );

  const handlePrevClick = useCallback(() => {
    if (!navigationState.isPrevDisabled) {
      onPageChange(currentPage - 1);
    }
  }, [navigationState.isPrevDisabled, onPageChange, currentPage]);

  const handleNextClick = useCallback(() => {
    if (!navigationState.isNextDisabled) {
      onPageChange(currentPage + 1);
    }
  }, [navigationState.isNextDisabled, onPageChange, currentPage]);

  return (
    <div className={toClassName(styles.paginationContainer, className)}>
      <Text variant="title3" className={styles.displayText}>
        {displayInfo}
      </Text>

      <div className={styles.actionsContainer}>
        <IconButton
          iconClassName={styles.actionIcon}
          onClick={handlePrevClick}
          disabled={navigationState.isPrevDisabled}
          disableRipple
          disableFocusRipple
          IconComponent={ArrowBackIcon}
        />
        <IconButton
          iconClassName={styles.actionIcon}
          onClick={handleNextClick}
          disabled={navigationState.isNextDisabled}
          disableRipple
          disableFocusRipple
          IconComponent={ArrowForwardIcon}
        />
      </div>
    </div>
  );
};

export default TransitionTasksTablePagination;
